/**
 * @file src/pages/company-onboarding-wizard/hooks/useOnboardingForm.js
 * @description Form state management for the company onboarding wizard.
 *
 * Responsibilities:
 *  - Hold formData, errors, currentStep, activeTab state
 *  - Provide updateFormData with automatic error clearing
 *  - Handle step navigation with validation gate
 *  - Persist and restore progress from localStorage
 *
 * Rules:
 *  - No API calls here — all side effects in useOnboardingActions.js
 *  - No JSX
 *  - Validation imported from utils/validation.js — not duplicated here
 */

import { useState, useCallback, useEffect } from "react";
import { INITIAL_FORM_DATA, STEPS, TABS } from "../constants";
import { validateOnboardingStep } from "utils/validation";
import { STORAGE_KEYS } from "config/constants";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {{ onFinalStep: () => void }} options
 *   onFinalStep — called by handleNext when all steps are complete and valid.
 *   Typically triggers handleSubmit in useOnboardingActions.
 */
const useOnboardingForm = ({ onFinalStep }) => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [activeTab, setActiveTab] = useState(TABS.CREATE);

  // ── Restore saved progress on mount ──────────────────────────────────────
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.COMPANY_ONBOARDING);
      if (!saved) return;

      const {
        formData: savedData,
        currentStep: savedStep,
        activeTab: savedTab,
      } = JSON.parse(saved);

      if (savedData) setFormData((prev) => ({ ...prev, ...savedData }));
      if (savedStep) setCurrentStep(savedStep);
      if (savedTab) setActiveTab(savedTab);
    } catch {
      // Corrupt storage — clear and start fresh
      localStorage.removeItem(STORAGE_KEYS.COMPANY_ONBOARDING);
    }
  }, []);

  // ── Persist progress whenever form changes ────────────────────────────────
  useEffect(() => {
    if (activeTab !== TABS.CREATE) return;
    try {
      localStorage.setItem(
        STORAGE_KEYS.COMPANY_ONBOARDING,
        JSON.stringify({ formData, currentStep, activeTab }),
      );
    } catch {
      // Storage quota exceeded — non-blocking
    }
  }, [formData, currentStep, activeTab]);

  // ── Field updater ─────────────────────────────────────────────────────────
  /**
   * Update a single top-level field and clear its error.
   * For nested fields like registrationNumber, pass the whole object:
   *   updateFormData("registrationNumber", { id: "123", schemeId: "CRN" })
   *
   * @param {string} field
   * @param {*}      value
   */
  const updateFormData = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error for this field — supports dot-notation keys
    setErrors((prev) => {
      const hasError = Object.keys(prev).some(
        (key) => key === field || key.startsWith(`${field}.`),
      );
      if (!hasError) return prev;

      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key === field || key.startsWith(`${field}.`)) {
          delete next[key];
        }
      });
      return next;
    });
  }, []);

  // ── Clear all errors ──────────────────────────────────────────────────────
  const clearErrors = useCallback(() => setErrors({}), []);

  // ── Tab switching ─────────────────────────────────────────────────────────
  const handleTabChange = useCallback(
    (tab) => {
      setActiveTab(tab);
      clearErrors();
    },
    [clearErrors],
  );

  // ── Step navigation ───────────────────────────────────────────────────────
  /**
   * Validate current step. If valid:
   *  - Advance to next step, OR
   *  - Call onFinalStep() if on the last step
   */
  const handleNext = useCallback(() => {
    const stepErrors = validateOnboardingStep(currentStep, formData);

    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    clearErrors();

    if (currentStep < STEPS.length) {
      setCurrentStep((prev) => prev + 1);
    } else {
      onFinalStep();
    }
  }, [currentStep, formData, onFinalStep, clearErrors]);

  /**
   * Go back one step and clear any errors.
   */
  const handlePrevious = useCallback(() => {
    if (currentStep > 1) {
      clearErrors();
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep, clearErrors]);

  // ── Set a global (non-field) error ────────────────────────────────────────
  /**
   * Used by useOnboardingActions to surface submit errors.
   * @param {string} message
   */
  const setSubmitError = useCallback((message) => {
    setErrors((prev) => ({ ...prev, submit: message }));
  }, []);

  // ── Clear persisted progress ──────────────────────────────────────────────
  const clearSavedProgress = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.COMPANY_ONBOARDING);
  }, []);

  // ── Reset form to initial state ───────────────────────────────────────────
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setCurrentStep(1);
    setActiveTab(TABS.CREATE);
    clearSavedProgress();
  }, [clearSavedProgress]);

  return {
    // State
    formData,
    errors,
    currentStep,
    activeTab,

    // Updaters
    updateFormData,
    handleNext,
    handlePrevious,
    handleTabChange,
    setSubmitError,
    clearSavedProgress,
    resetForm,
  };
};

export default useOnboardingForm;
